<template>
    <div class="firm-order-form">
      <vs-collapse type="border" accordion class="p-0">
        <vs-collapse-item open>
          <div slot="header">
            <div class="accordion-header">
              <div class="number">1</div>
              <div class="label">{{ $t('SubmissionType') }}</div>
              <div class="hint">{{  $t('StandardSignature') }}</div>
              <div class="hint-expanded" :class="{'complete' : !disableUpload}">
                {{ $t('SelectSignature') }}
                <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <!-- Content Row -->
          <div class="vx-row">
            <div class="vx-col">
              <custom-radio-large v-model="form.SubmissionType" :data="customRadioData"/>
            </div>
          </div>
        </vs-collapse-item>

        <vs-collapse-item ref="uploadRow" class="upload-row" :disabled="disableUpload">
          <div slot="header">
            <div class="accordion-header">
              <div class="number">2</div>
              <div class="label">{{ $t('Upload') }}</div>
              <div class="hint">{{ $t('TypeOfSubmissionRequired') }}</div>
              <div class="hint-expanded" :class="{'complete' : !disableDetails}">
                {{ uploadHint }}
                <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <div class="vx-col w-full">
            <div class="vx-row">
              <div class="vx-col w-full">
                <div class="inner-accordion-hint">{{ uploadText }}</div>
              </div>
              <div v-if="isRemoteType" class="vx-col w-full">
                <input-file-drag-drop
                    ref="remoteTypeDrop"
                    v-model="form.RemoteTypeFiles"
                    :max-files="maxFiles.remoteType"
                    accepted-files="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12"
                />
              </div>
              <div v-if="isStandardType" class="vx-col w-full">
                <input-file-drag-drop
                    ref="standardTypeDrop"
                    v-model="form.StandardTypeFiles"
                    :max-files="maxFiles.standardType"
                />
              </div>
            </div>
          </div>
        </vs-collapse-item>

        <vs-collapse-item ref="emailAddressRow" class="email-address-row" :disabled="disableDetails">
          <div slot="header">
            <div class="accordion-header">
              <div class="number">3</div>
              <div class="label">{{ $t('EmailAddress') }}</div>
              <div class="hint">{{ $t('CompleteStep2') }}</div>
              <div class="hint-expanded" :class="{'complete' : !disableDisclaimer}">
                {{ $t('AddYourEmailAddress') }}
                 <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <div class="vx-col w-full">
            <div class="vx-row">
              <div class="vx-col w-full">
                <div class="inner-accordion-hint">{{ $t('EmailAddressCertificate') }}</div>
              </div>
              <div class="vx-col w-full lg:w-5/12 mt-4">
                <label class="vs-input--label">{{ $t('EnterYourEmailAddress') }}</label>
                <v-select v-model="form.EmailAddress"
                          :clearable="false"
                          :options="emailOptions"
                          name="EmailAddress"
                          :data-vv-as="$t('EnterYourEmailAddress')"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
              </div>
              <div class="vx-col w-full lg:w-1/4 mt-4">
                <label class="vs-input--label">{{ $t('CountryCode') }}</label>
                <v-select v-model="country_code_local"
                          :clearable="false"
                          :options="countryOptions"
                          name="ContactNumberCountryCode"
                          :data-vv-as="$t('CountryCode')"
                          :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
              </div>
              <div class="vx-col w-full lg:w-1/3 mt-4">
                <label class="vs-input--label">{{ $t('TelephoneNumber') }}</label>
                <vs-input class="w-full"
                          v-model="form.ContactNumber"
                          name="ContactNumber"
                          :data-vv-as="$t('TelephoneNumber')"
                          type="number"/>
              </div>

              <div class="vx-col w-full mt-4">
                <label class="vs-input--label">{{ $t('AdditionalEmailAddresses') }}</label>
                <multiselect
                    v-model="form.EmailAddresses"
                    :options="emailOptions"
                    :multiple="true"
                    :show-labels="false"
                    :placeholder="$t('SelectOption')"
                >
                  <template slot="selection" slot-scope="{ values, search, isOpen }">
                    <span class="multiselect__single" v-if="values.length && !isOpen">
                      {{ values.length }} emails selected
                    </span>
                  </template>
                </multiselect>

                <div class="additional-emails">
                  <div
                      v-if="form.EmailAddresses && form.EmailAddresses.length"
                      v-for="(emailAddress, index) in form.EmailAddresses"
                      class="additional-email"
                  >
                    <div class="email">
                      <span>{{ emailAddress }}</span>
                    </div>
                    <div class="remove-button" @click="removeEmail(index)">
                      <font-awesome-icon size="lg" class="remove-icon" :icon="['fal', 'times']"/>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            <div class="vx-row mt-6">
              <div class="vx-col w-full">
                <vs-checkbox v-model="form.SendCertificate"
                             name="SendCertificate"
                  >{{ $t('SendCertificateToProposer') }}</vs-checkbox>
              </div>
            </div>
          </div>
        </vs-collapse-item>

        <vs-collapse-item v-if="showDisclaimer" :disabled="disableNote" >
          <div slot="header">
            <div class="accordion-header">
              <div class="number">4</div>
              <div class="label">{{ $t('Note') }}</div>
              <div class="hint">{{ $t('CompleteStep3') }}</div>
              <div class="hint-expanded" :class="{'complete' : noteAdded}">
                {{ $t('AddNote') }}
                 <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <div class="vx-col w-full">
            <div class="vx-row">
              <div class="vx-col w-full">
                <p>{{ $t('AdditionalNotes') }}:</p>
                <vs-textarea
                  height="150px"
                  :label="$t('InsertAdditionalNotes')"
                  v-model="form.Note"/>
              </div>
            </div>
          </div>
        </vs-collapse-item>

        <vs-collapse-item v-if="showDisclaimer" :disabled="disableDisclaimer" >
          <div slot="header">
            <div class="accordion-header">
              <div class="number">5</div>
              <div class="label">{{ $t('Disclaimer') }}</div>
              <div class="hint">{{ $t('CompleteStep4') }}</div>
              <div class="hint-expanded" :class="{'complete' : formComplete}">
                {{ $t('ReadTheDisclaimer') }}
                 <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <div class="vx-col w-full">
            <div class="vx-row">
              <div class="vx-col w-full">
                <checkbox-large v-model="form.agreed1">
                  {{ $t('DisclaimerText1') }}
                </checkbox-large>
              </div>
              <div class="vx-col w-full mt-4">
                <checkbox-large v-model="form.agreed2">
                  {{ $t('DisclaimerText2') }}
                </checkbox-large>
              </div>
            </div>
          </div>
        </vs-collapse-item>
      </vs-collapse>
      <portal to="form-buttons">
            <form-order-form-buttons :loading="loading || !formComplete" @submit="submitForm" @reset="resetForm" />
        </portal>

    </div>
</template>

<script>
import vSelect from 'vue-select';
import CountryList from 'countries-list';
import FirmOrderSubForm from "./FirmOrderSubForm";
import FormOrderFormButtons from "./FormOrderFormButtons";
import InputFile from "../../components/file-upload/InputFile";
import InputFileDragDrop from "../../components/file-upload/InputFileDragDrop";
import HasLoadingMixin from '@/components/mixins/HasLoadingMixin';
import RadioLarge from '../../components/custom-form-fields/radio-large';
import CheckboxLarge from '../../components/custom-form-fields/CheckboxLarge';
import CustomRadioLarge from '../../components/custom-form-fields/CustomRadioLarge';

export default {
  name: "FirmOrderForm",
  components: {
    RadioLarge,
    CustomRadioLarge,
    CheckboxLarge,
    vSelect,
    FirmOrderSubForm,
    FormOrderFormButtons,
    InputFile,
    InputFileDragDrop,
  },
  mixins: [CountryList, HasLoadingMixin],
  data() {
    return {
      form: {
        EmailAddress: '',
        Note: '',
      },
      customRadioData: [
        {
          label: this.$t('StandardSignature'),
          text: this.$t('StandardSignatureText'),
          value: "0",
        },
        {
          label: this.$t('RemoteSignature'),
          text: this.$t('RemoteSignatureText'),
          value: "1",
        },
      ],
      remoteForm: {},
      standardForm: {},
      maxFiles: {
        remoteType: 1,
        standardType: 2,
      },
      titleOptions: [
        'Dr',
        'Mr',
        'Mrs',
        'Ms',
        'Miss',
        'Professor',
      ],
      emailAddresses: [],
    }
  },
  computed: {
    uploadText() {
      if (this.isStandardType) {
        return this.$t('UploadFormExcelProposal')
      }

      return this.$t('UploadFormExcel')
    },
    uploadHint() {
      if (this.isStandardType) {
        return this.$t('AddYourFilePlural');
      }

      return this.$t('AddYourFileSingular');
    },
    disableUpload() {
      return !this.form.SubmissionType;
    },
    disableDetails() {
      if (!this.disableUpload) {
        if (this.form.RemoteTypeFiles
            && this.isRemoteType
            && this.form.RemoteTypeFiles.length === this.maxFiles.remoteType
        ) {
          return false;
        }

        if (this.form.StandardTypeFiles
            && this.isStandardType
            && this.form.StandardTypeFiles.length === this.maxFiles.standardType
        ) {
          return false;
        }
      }

      return true;
    },
    showDisclaimer() {
      return !this.getSubmissionType;
    },
    disableNote() {
      return this.disableDetails
          || !this.form.EmailAddress
          || !this.form.ContactNumber
          || !this.country_code_local;
    },
    noteAdded() {
      return !! this.form.Note;
    },
    disableDisclaimer() {
      return this.disableNote;
    },
    formComplete() {
      if (!this.showDisclaimer) {
        return !this.disableDisclaimer;
      }

      return !this.disableDisclaimer
          && this.form.agreed1
          && this.form.agreed2
    },
    countryOptions() {
      const countryList = [];

      for (const [key, country] of Object.entries(CountryList.countries)) {
        countryList.push({
          label: country.name,
          value: country.phone,
        });
      }

      return countryList;
    },
    emailOptions() {
      if (!this.emailAddresses || this.emailAddresses.length === 0) {
        return [];
      }

      return this.emailAddresses.map((item) => {
        return item.EmailAddress;
      });
    },
    getSubmissionType() {
      return parseInt(this.form.SubmissionType);
    },
    isRemoteType() {
      return this.getSubmissionType === 1;
    },
    isStandardType() {
      return this.getSubmissionType === 0;
    },
    country_code_local: {
      get() {
        return this.countryOptions.find(({ value }) => value === this.form.ContactNumberCountryCode);
      },
      set(val) {
        this.$set(this.form, 'ContactNumberCountryCode', val.value);
      }
    },
    ExcelRaterFile() {
      return this.remoteForm.ExcelRaterFile;
    }
  },
  created() {
    // Pre-populate email address field with the current logged in user's
    const user = this.$store.getters['auth/user'];
    this.form.EmailAddress = user.email;

    this.fetchData();
  },
  methods: {
    removeEmail(i) {
      this.form.EmailAddresses.splice(i, 1);
    },
    async fetchData() {
      await this.getUsers();
    },
    clearValidation(field) {
      console.log(field);
      this.$validator.errors.remove(field);
    },
    addValidationError(field, value) {
      this.clearValidation(field);

      this.$validator.errors.add({
        field: field,
        msg: value,
      })
    },
    async getUsers() {
      try {
        const { data } = await this.$api.emailAddresses.list();
        this.emailAddresses = data.data;
      } catch (e) {
        this.$vs.notify({
          title: this.$t('ErrorOccurred'),
          text: this.$t('UsersFetchFailureMessage'),
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right'
        });
      }
    },
    optionalFieldLabel(fieldName) {
      return this.$t(fieldName) + ' (' + this.$t('Optional') + ')';
    },
    async getParams() {
      let files;
      let note;

      if (this.isRemoteType) {
        files = this.form.RemoteTypeFiles;
        note = '';
      }

      if (this.isStandardType) {
        files = this.form.StandardTypeFiles;
        note = this.form.Note;
      }

      if (files && !files.length) {
        this.$vs.notify({
          title: 'Error',
          text: `No files found`,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: "top-right"
        })

        return {};
      }

      let excelRaterFile = null; // XLSX
      let proposalFormFile = null; // PDF

      files.forEach((file) => {
        if (file.type !== 'application/pdf') {
          excelRaterFile = file;
        } else if (this.isStandardType) {
          proposalFormFile = file;
        }
      })

      const params = new FormData();
      params.append('Language', this.$i18n.locale);
      params.append('ContactNumberCountryCode', this.form.ContactNumberCountryCode);
      params.append('ContactNumber', this.form.ContactNumber);
      params.append('EmailAddress', this.form.EmailAddress);
      params.append('EmailAddressCC', this.form.EmailAddresses);
      params.append('SendCertificate', this.form.SendCertificate);
      params.append('Agreed', true);
      params.append('SubmissionType', Number(this.form.SubmissionType));
      params.append('ExcelRaterFile', excelRaterFile);
      params.append('ProposalFormFile', proposalFormFile);
      params.append('Files', null);
      params.append('Note', note);

      return params;
    },
    async isValid() {
      return this.$validator.validateAll();
    },
    async submitForm() {
      const params = await this.getParams();
      const valid = await this.isValid();

      if (!valid) {
        return false;
      }

      try {
        this.enableLoading();
        const { data } = await this.$api.submitRater.create(params);

        const { ClientReference, Message } = data.data;

        const success = (ClientReference) ? true : false;

        if (success) {
          await this.$router.push({name: 'upload_firm_orders.success', params: { clientReference:ClientReference }})
        } else {
          await this.$router.push({name: 'upload_firm_orders.error'})
        }

      } catch(e) {
        const { Message, Errors } = e.response.data.data;
        if (Errors) {
          this.addErrors(e);
        }
        this.$vs.notify({
          title: this.$t('ErrorOccurred'),
          text: (Message) ? Message : this.$t('UploadFailureMessage'),
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right'
        });
      } finally {
        this.disableLoading();
      }
    },
    resetForm() {
      this.form = {};
      this.$validator.reset();
      this.errors.clear();
    },
    addErrors(e) {
      if (!e.response.data.data.Errors) {
        return;
      }

      const errorBag = Object.entries(e.response.data.data.Errors)
          .filter((item) => item[1] !== null)
          .map((errorBag) => {
            return {
              field: errorBag[0],
              msg: errorBag[1][0]
            };
          });

      const addErrorToForms = (component, forms, {field, msg}) => {
        component.$validator.errors.add();

        forms.forEach((ref) => {
          const child = component.$refs[ref];

          if (!child) {
            return true;
          }

          // Child forms
          const childForms = child.$data['forms'] || [];
          addErrorToForms(child, childForms, {field, msg});
        });
      };

      errorBag.forEach((error) => {
        addErrorToForms(this, this.forms || [], error);
      });
    },
  }
}
</script>

<style lang="scss">
@import '../../../assets/scss/custom/components/variables';
  .vs-input--label {
    font-size: 0.9rem;

    &.text-large {
      font-size: 1.2rem;
    }
  }

  .firm-order-form {
    .multiselect__content-wrapper {
      z-index: 999;
    }

    .vs-collapse-item {
      cursor: initial;

      .accordion-header {
        display: flex;
        flex-direction: row;
        align-items: center;

        .number {
          display: inline-block;
          width: 22px;
          height: 22px;
          background-color: $yellow;
          text-align: center;
          border-radius: 50%;
          font-size: 15px;
          font-weight: bold;
          color: #000;
        }

        .label {
          display: inline-block;
          margin-left: 11px;
          font-size: 22px;
          color: #262626;
        }

        .hint,
        .hint-expanded {
          margin-left: auto;
          padding-right: 15px;
          font-size: 14px;
        }

        .hint {
          display: none;
        }

        .hint-expanded {
          &.complete {
            font-weight: bold;

            > svg {
              display: initial;
            }
          }

          > svg {
            display: none;
            color: $green;
          }
        }
      }

      .inner-accordion-hint {
        font-size: 15px;
        color: #262626;
      }

      .vs-collapse-item--content {
        .con-content--item {
          padding-top: 0;
        }
      }

      &.upload-row {
        &.open-item {
          .vs-collapse-item--content {
            max-height: 400px !important;
          }
        }
      }

      &.email-address-row {
        &.open-item {
          .vs-collapse-item--content {
            overflow: visible;
            max-height: 400px !important;

            @media (max-width: 1199px) {
              max-height: 8000px !important;
            }
          }
        }

        .additional-emails {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-start;
          align-items: center;

          .additional-email {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            border: 1px solid $grey-light;
            border-radius: 5px;
            font-size: 13px;
            margin-top: 6px;
            margin-right: 6px;

            .email {
              padding: 6px 12px;
              background-color: $grey-lightest;
            }

            .remove-button {
              width: 25px;
              height: 31px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-left: 1px solid $grey-light;

              &:hover {
                background-color: $grey-lightest;
              }
            }
          }
        }
      }

      &.disabledx {
        opacity: 1;

        .number {
          color: rgba(68, 75, 87, 0.7);
          background-color: rgba(38, 38, 38, 0.2);
        }

        .hint,
        .hint-expanded {
          color: rgba(68, 75, 87, 0.7);
        }

        .hint {
          display: initial;
        }

        .hint-expanded {
          display: none;
        }
      }
    }
  }
</style>
